import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { AuthContext } from "contexts/AuthContext";
import { StepDetails } from "components/TaskInstructionSlices/StepDetails";
import { Layout } from "components/Layout";
import { Modal } from "components/Modal";
import { Button, ButtonVariant } from "components/Form/Button";
import { AppRoutes, PrismicCollectionDetailField } from "types";
import {
  isMetadataFieldId,
  metadataFields,
  normalizeMetadataValue,
} from "lib/metadata-qr";
import { resolveSessionMetadataFields } from "lib/prismic-labels";
import {
  clearSessionMetadata,
  loadSessionMetadata,
  saveSessionMetadata,
  sessionMetadataOwner,
} from "lib/session-metadata";
import { SessionMetadataContext } from "./context";

const noKnownValues = {};
const noDetails: string[] = [];

function SessionForm({
  fields,
  values,
  onSave,
  onCancel,
  starting = false,
}: {
  fields: PrismicCollectionDetailField[];
  values: Record<string, string>;
  onSave: (values: Record<string, string>) => void;
  onCancel: () => void;
  starting?: boolean;
}) {
  const draft = useRef(values);
  const validate = useRef<() => Array<{ id: string; title: string }>>(() => []);
  const [error, setError] = useState("");
  const initialDetails = useMemo(
    () => Object.entries(values).map(([id, value]) => `${id}: ${value}`),
    [values]
  );
  const getDetails = useCallback(
    () => (initialDetails.length ? initialDetails : noDetails),
    [initialDetails]
  );
  const onChange = useCallback(
    (
      _step: number,
      _details: string[],
      fields?: Array<{ label_id: string; value: string }>
    ) => {
      draft.current = Object.fromEntries(
        (fields || []).map((field) => [field.label_id, field.value])
      );
    },
    []
  );
  const register = useCallback((fn: typeof validate.current) => {
    validate.current = fn;
  }, []);
  return (
    <div
      className="space-y-6"
      role="dialog"
      aria-modal="true"
      aria-label="Session details"
    >
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold">
          {starting ? "Before you start" : "Session details"}
        </h2>
        <p className="text-sm text-858494">
          {starting
            ? "Set these details once. We’ll use them for every task in this session. You can change them later."
            : "Shared across tasks in this session. Changes apply to future captures; completed captures keep their original details."}
        </p>
      </div>
      <StepDetails
        stepDetailsFields={fields}
        stepNumber={0}
        dropdownValueSide="value"
        inlineQrPrefill
        knownTaskValues={noKnownValues}
        getStepDetails={getDetails}
        onStepDetailsChange={onChange}
        onValidationRegister={register}
      />
      {error && (
        <p role="alert" className="text-fd380f text-sm">
          {error}
        </p>
      )}
      <Button
        type="button"
        className="w-full"
        onClick={() => {
          if (validate.current().length) return;
          try {
            onSave(draft.current);
          } catch (problem) {
            setError(
              problem instanceof Error
                ? problem.message
                : "Session details could not be saved."
            );
          }
        }}
      >
        {starting ? "Continue to task" : "Save session details"}
      </Button>
      <Button
        type="button"
        className="w-full"
        variant={ButtonVariant.FLAT}
        onClick={onCancel}
      >
        Cancel
      </Button>
    </div>
  );
}

export function SessionMetadataProvider({ children }: PropsWithChildren) {
  const { config } = useContext(CollectionDetailsContext);
  const { token } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const rawFields = config?.selectedCollection?.data.session_details;
  const configured = useMemo(
    () =>
      (rawFields || []).filter((field) =>
        Boolean(
          field.label_id?.toString().trim() ||
            field.label_title?.toString().trim() ||
            field.dropdown_options?.toString().trim() ||
            (field.label_document as { id?: string })?.id ||
            field.is_required ||
            field.qr_prefill
        )
      ),
    [rawFields]
  );
  const documentId = config?.selectedCollection?.id || "";
  const owner = sessionMetadataOwner();
  const key = JSON.stringify([owner, documentId, configured]);
  const [loaded, setLoaded] = useState<{
    key: string;
    fields: PrismicCollectionDetailField[];
    values: Record<string, string>;
    initialized: boolean;
    error?: string;
  }>();
  const [editing, setEditing] = useState(false);
  const [pendingStart, setPendingStart] = useState<(() => void) | null>(null);
  useEffect(() => {
    if (config === undefined) return;
    let cancelled = false;
    setEditing(false);
    setPendingStart(null);
    if (!configured?.length || !owner || !token) {
      clearSessionMetadata();
      return;
    }
    resolveSessionMetadataFields(configured)
      .then((fields) => {
        const definitions = metadataFields(fields);
        const ids = definitions.map((field) => field.id);
        if (
          ids.some((id) => !isMetadataFieldId(id)) ||
          new Set(ids).size !== ids.length ||
          fields.some((field) => field.use_as_step_labels)
        )
          throw new Error(
            "Session details need distinct label IDs and cannot be used as step labels."
          );
        const saved = loadSessionMetadata(documentId);
        const values = Object.fromEntries(
          definitions.flatMap((field) => {
            const value = saved?.values[field.id];
            if (!value) return [];
            try {
              return [[field.id, normalizeMetadataValue(field, value)]];
            } catch {
              return [];
            }
          })
        );
        if (!cancelled)
          setLoaded({
            key,
            fields,
            values,
            initialized:
              saved?.schema === JSON.stringify(definitions) &&
              JSON.stringify(values) === JSON.stringify(saved?.values) &&
              definitions.every(
                (field) => !field.required || Boolean(values[field.id])
              ),
          });
      })
      .catch((problem) => {
        if (!cancelled)
          setLoaded({
            key,
            fields: [],
            values: {},
            initialized: false,
            error:
              problem instanceof Error
                ? problem.message
                : "Session details could not be loaded.",
          });
      });
    return () => {
      cancelled = true;
    };
  }, [config, configured, documentId, key, owner, token]);
  const active = Boolean(configured?.length && owner && token);
  const current = loaded?.key === key ? loaded : undefined;
  const fields = useMemo(
    () => metadataFields(current?.fields || []),
    [current?.fields]
  );
  useEffect(() => {
    if (pendingStart && current?.initialized) {
      setPendingStart(null);
      pendingStart();
    }
  }, [pendingStart, current?.initialized]);
  const startTask = useCallback(
    (onReady: () => void) => {
      if (!active || current?.initialized) onReady();
      else setPendingStart(() => onReady);
    },
    [active, current?.initialized]
  );
  const taskRoute = Boolean(
    location.state?.taskId &&
      ![AppRoutes.home, AppRoutes.logout, AppRoutes.welcome].includes(
        location.pathname as AppRoutes
      )
  );
  const save = (values: Record<string, string>) => {
    const saved = saveSessionMetadata(documentId, fields, values);
    setLoaded(
      (previous) =>
        previous && { ...previous, values: saved.values, initialized: true }
    );
    setEditing(false);
  };
  const cancel = () => {
    setEditing(false);
    setPendingStart(null);
    if (!current?.initialized && taskRoute) navigate(AppRoutes.home);
  };
  const form = current && (
    <SessionForm
      fields={current.fields}
      values={current.values}
      onSave={save}
      onCancel={cancel}
      starting={Boolean(pendingStart)}
    />
  );
  if (
    active &&
    taskRoute &&
    (!current || current.error || !current.initialized)
  )
    return (
      <Layout>
        <div className="space-y-6">
          {!current ? (
            <p>Loading session details...</p>
          ) : current.error ? (
            <>
              <p role="alert" className="text-fd380f">
                {current.error}
              </p>
              <Button to={AppRoutes.home}>Back to tasks</Button>
            </>
          ) : (
            form
          )}
        </div>
      </Layout>
    );
  return (
    <SessionMetadataContext.Provider
      value={{
        fields: active && current?.initialized ? fields : [],
        values: active ? current?.values || {} : {},
        edit: () => setEditing(true),
        startTask,
      }}
    >
      {children}
      <Modal
        show={active && (editing || Boolean(pendingStart))}
        onClose={cancel}
      >
        {!current ? (
          <p>Loading session details...</p>
        ) : current.error ? (
          <div className="space-y-4">
            <p role="alert" className="text-fd380f">
              {current.error}
            </p>
            <Button type="button" onClick={cancel}>
              Back to tasks
            </Button>
          </div>
        ) : (
          form
        )}
      </Modal>
    </SessionMetadataContext.Provider>
  );
}
